import React, { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppDispatch } from '../../store';
import { uploadDocuments } from '../../store/actions/BatchTables/BatchTablesActions';
import { hideSidePanel } from '../../store/actions/sidePanelActions';
import { showFeedbackModal } from '../../store/actions/UserFeedbackActions';
import FileUploader from '../shared/TailwindComponents/FileUploader';
import Button from '../shared/TailwindComponents/Button';
import { PiUploadSimpleLight } from 'react-icons/pi';

interface AddTableDocumentPanelProps {
  tableUuid: string;
  onClose: () => void;
}

const AddTableDocumentPanel: React.FC<AddTableDocumentPanelProps> = ({ tableUuid, onClose }) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const [files, setFiles] = useState<File[]>([]);
  const [isUploading, setIsUploading] = useState(false);

  const handleFilesChange = useCallback((newFiles: File[]) => {
    setFiles(newFiles);
  }, []);

  const handleUpload = useCallback(async () => {
    if (files.length === 0 || !tableUuid) return;

    setIsUploading(true);
    try {
      await dispatch(uploadDocuments(tableUuid, files) as any);
      dispatch(showFeedbackModal({
        showModal: true,
        message: t('Documents uploaded successfully'),
        modalType: 'success',
        duration: 3000,
      }));
      dispatch(hideSidePanel());
    } catch (error) {
      dispatch(showFeedbackModal({
        showModal: true,
        message: t('Failed to upload documents'),
        modalType: 'error',
        duration: 5000,
      }));
    } finally {
      setIsUploading(false);
    }
  }, [dispatch, files, tableUuid, t]);

  return (
    <div className="flex flex-col h-full">
      <div className="flex-1 overflow-y-auto p-6">
        <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
          {t('Select documents to upload to this table. Supported formats include PDF, DOCX, XLSX, and CSV.')}
        </p>
        <FileUploader
          files={files}
          onFilesChange={handleFilesChange}
        />
      </div>

      <div className="border-t border-gray-200 dark:border-gray-700 px-6 py-4 flex justify-end gap-3">
        <Button
          variant="outline"
          size="small"
          onClick={onClose}
          disabled={isUploading}
        >
          {t('Cancel')}
        </Button>
        <Button
          variant="primary"
          size="small"
          onClick={handleUpload}
          disabled={files.length === 0 || isUploading}
          icon={<PiUploadSimpleLight className="w-4 h-4" />}
        >
          {isUploading ? t('Uploading...') : t('Upload')}
        </Button>
      </div>
    </div>
  );
};

export default AddTableDocumentPanel;
